/**
 * BAR-1564: sign-in through BARK's own Keycloak (EU) instead of Firebase Auth.
 *
 * Active only when config.json says `"authProvider": "keycloak"` and names the
 * issuer and client (start-nginx.sh writes all three from AUTH_PROVIDER,
 * OIDC_ISSUER and OIDC_CLIENT_ID). Anything else leaves the app on Firebase,
 * exactly as before: `configureOidc` is called by apiService.initialize(), and
 * until then (or without the keys) `isKeycloakMode()` is false.
 *
 * One UserManager per page: Authorization Code + PKCE against Keycloak's hosted
 * login page, refresh with the refresh token, sign-out through Keycloak's
 * end-session endpoint. The refresh token never leaves oidc-client-ts's store.
 * App keeps owning WHEN to refresh (refreshAccessToken, as with Firebase), so
 * the automatic renew timer stays off and the access token is mirrored into the
 * existing `bark_auth_token` key by the callers, never by an event here.
 */
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { terminalAuthRefreshError } from '../apiErrors';

/** Keycloak sends the browser back here after sign-in (and after kc_action). */
export const OIDC_CALLBACK_PATH = '/auth/callback';

/**
 * What `bark_refresh_token` holds in keycloak mode. The real refresh token stays
 * in oidc-client-ts's store; the marker keeps the code that asks "can this
 * session refresh?" (startup restore, impersonation stash) working unchanged,
 * and apiService.refreshToken() refreshes through the UserManager instead.
 */
export const OIDC_SESSION_MARKER = 'oidc-session';

/** Custom state carried through the Keycloak redirect and back. */
export interface OidcLoginState {
    /** Same-origin path (with query and hash) to open after sign-in. */
    returnTo: string;
    /** The signup consent the sign-in page showed (free account via Google/Microsoft). */
    signupConsentVersion?: string;
    /** Google or Microsoft, when the sign-in page sent the person straight there. */
    idpHint?: 'google' | 'microsoft';
    /** Set when the redirect was Keycloak's UPDATE_PASSWORD action, not a sign-in. */
    action?: 'UPDATE_PASSWORD';
}

export interface OidcCallbackResult {
    accessToken: string;
    state: OidcLoginState;
    /** Keycloak's `kc_action_status` (`success`, `cancelled`, `error`), or null. */
    kcActionStatus: string | null;
}

export interface OidcTokens {
    access_token: string;
    refresh_token: string;
    expires_in: number;
}

type OidcSettings = { issuer: string; clientId: string };

let settings: OidcSettings | null = null;
let manager: UserManager | null = null;

const configString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * Read the auth keys of config.json. Keycloak mode needs all three; with the
 * provider set but the issuer or client missing, the app stays on Firebase and
 * says why in the console (start-nginx.sh refuses that combination too).
 */
export function configureOidc(config: { authProvider?: unknown; oidcIssuer?: unknown; oidcClientId?: unknown }): void {
    const provider = configString(config.authProvider).toLowerCase();
    const issuer = configString(config.oidcIssuer).replace(/\/+$/, '');
    const clientId = configString(config.oidcClientId);
    if (provider !== 'keycloak') {
        settings = null;
        manager = null;
        return;
    }
    if (!issuer || !clientId) {
        console.error('[Auth] authProvider is keycloak but oidcIssuer or oidcClientId is missing; staying on Firebase');
        settings = null;
        manager = null;
        return;
    }
    if (settings && settings.issuer === issuer && settings.clientId === clientId) return;
    settings = { issuer, clientId };
    manager = null;
}

/** True once config.json has put the app on Keycloak. */
export function isKeycloakMode(): boolean {
    return settings !== null;
}

/** Test seam: forget the configuration and the UserManager. */
export function __resetOidcForTests(): void {
    if (process.env.NODE_ENV !== 'test') return;
    settings = null;
    manager = null;
}

function userManager(): UserManager {
    if (!settings) throw new Error('Keycloak sign-in is not configured');
    if (!manager) {
        const { issuer, clientId } = settings;
        const origin = window.location.origin;
        manager = new UserManager({
            authority: issuer,
            client_id: clientId,
            redirect_uri: `${origin}${OIDC_CALLBACK_PATH}`,
            post_logout_redirect_uri: `${origin}/`,
            response_type: 'code',
            scope: 'openid email profile',
            // Keycloak's endpoints are fixed paths under the issuer: no discovery round trip.
            metadata: {
                issuer,
                authorization_endpoint: `${issuer}/protocol/openid-connect/auth`,
                token_endpoint: `${issuer}/protocol/openid-connect/token`,
                end_session_endpoint: `${issuer}/protocol/openid-connect/logout`,
                userinfo_endpoint: `${issuer}/protocol/openid-connect/userinfo`,
                jwks_uri: `${issuer}/protocol/openid-connect/certs`,
                revocation_endpoint: `${issuer}/protocol/openid-connect/revoke`,
            },
            loadUserInfo: false,
            automaticSilentRenew: false,
            monitorSession: false,
            // localStorage, like the Firebase session before it: a sign-in survives a browser restart.
            userStore: new WebStorageStateStore({ store: window.localStorage, prefix: 'bark_oidc.' }),
            stateStore: new WebStorageStateStore({ store: window.localStorage, prefix: 'bark_oidc_state.' }),
        });
    }
    return manager;
}

/**
 * A same-origin path to return to, or `/`. Refuses absolute and
 * protocol-relative URLs, and the callback itself (it would loop).
 */
export function safeReturnPath(value?: string | null): string {
    if (typeof value !== 'string' || !value.startsWith('/')) return '/';
    if (value.startsWith('//') || value.startsWith('/\\')) return '/';
    if (value === OIDC_CALLBACK_PATH || value.startsWith(`${OIDC_CALLBACK_PATH}?`) || value.startsWith(`${OIDC_CALLBACK_PATH}#`)) return '/';
    return value;
}

export function isOidcCallbackPath(pathname: string): boolean {
    return pathname === OIDC_CALLBACK_PATH;
}

/** The connector hand-off (`?connect=…&gateway_origin=…`) as a path to return to. */
export function connectReturnPath(state: string | null, gatewayOrigin: string | null): string | null {
    if (!state) return null;
    const params = new URLSearchParams({ connect: state });
    if (gatewayOrigin) params.set('gateway_origin', gatewayOrigin);
    return `/?${params.toString()}`;
}

function normalizeState(value: unknown): OidcLoginState {
    const raw = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
    const state: OidcLoginState = { returnTo: safeReturnPath(typeof raw.returnTo === 'string' ? raw.returnTo : '/') };
    if (typeof raw.signupConsentVersion === 'string' && raw.signupConsentVersion) {
        state.signupConsentVersion = raw.signupConsentVersion;
    }
    if (raw.idpHint === 'google' || raw.idpHint === 'microsoft') state.idpHint = raw.idpHint;
    if (raw.action === 'UPDATE_PASSWORD') state.action = 'UPDATE_PASSWORD';
    return state;
}

/**
 * Send the browser to Keycloak's sign-in page. `idpHint` goes straight to
 * Google or Microsoft (`kc_idp_hint`); `forceLogin` asks for credentials even
 * when Keycloak still has a session (after BARK refused that account).
 * `replace` swaps the history entry instead of pushing one, so Back from
 * Keycloak does not land on a page that only forwards again.
 */
export async function startKeycloakLogin(options: {
    returnTo?: string;
    idpHint?: 'google' | 'microsoft';
    signupConsentVersion?: string;
    forceLogin?: boolean;
    replace?: boolean;
} = {}): Promise<void> {
    const state: OidcLoginState = { returnTo: safeReturnPath(options.returnTo) };
    if (options.signupConsentVersion) state.signupConsentVersion = options.signupConsentVersion;
    if (options.idpHint) state.idpHint = options.idpHint;
    await userManager().signinRedirect({
        state,
        ...(options.idpHint ? { extraQueryParams: { kc_idp_hint: options.idpHint } } : {}),
        ...(options.forceLogin ? { prompt: 'login' } : {}),
        ...(options.replace ? { redirectMethod: 'replace' as const } : {}),
    });
}

/** Change the password on Keycloak's own form (`kc_action=UPDATE_PASSWORD`). */
export async function startKeycloakPasswordChange(returnTo?: string): Promise<void> {
    const state: OidcLoginState = { returnTo: safeReturnPath(returnTo), action: 'UPDATE_PASSWORD' };
    await userManager().signinRedirect({
        state,
        extraQueryParams: { kc_action: 'UPDATE_PASSWORD' },
    });
}

/** Finish the redirect on /auth/callback: exchange the code and keep the tokens. */
export async function completeKeycloakLogin(url: string = window.location.href): Promise<OidcCallbackResult> {
    let kcActionStatus: string | null = null;
    try {
        kcActionStatus = new URL(url).searchParams.get('kc_action_status');
    } catch {
        kcActionStatus = null;
    }
    const mgr = userManager();
    const user = await mgr.signinRedirectCallback(url);
    // Sign-ins started and abandoned leave their state behind; drop the stale ones.
    void mgr.clearStaleState().catch(() => undefined);
    return { accessToken: user.access_token, state: normalizeState(user.state), kcActionStatus };
}

/**
 * Refresh through the UserManager (the refresh token grant). Keycloak refusing
 * the refresh token (session ended, user disabled or deleted) is terminal: the
 * stored session is dropped and the error carries status 401, which the
 * refresh owners treat as "sign in again". A network failure is not.
 */
export async function refreshKeycloakTokens(): Promise<OidcTokens> {
    const mgr = userManager();
    const current = await mgr.getUser();
    if (!current?.refresh_token) {
        throw terminalAuthRefreshError('No Keycloak session to refresh');
    }
    let refreshed: User | null;
    try {
        refreshed = await mgr.signinSilent();
    } catch (error: unknown) {
        if ((error as { name?: unknown })?.name === 'ErrorResponse') {
            await mgr.removeUser().catch(() => undefined);
            throw terminalAuthRefreshError('Keycloak refused the refresh token');
        }
        throw error;
    }
    if (!refreshed?.access_token) {
        throw terminalAuthRefreshError('Keycloak returned no access token');
    }
    return {
        access_token: refreshed.access_token,
        refresh_token: OIDC_SESSION_MARKER,
        expires_in: refreshed.expires_in ?? 0,
    };
}

/** The stored ID token (the `id_token_hint` of a sign-out), or null. */
export async function currentKeycloakIdToken(): Promise<string | null> {
    try {
        return (await userManager().getUser())?.id_token ?? null;
    } catch {
        return null;
    }
}

/** Forget the stored session in this browser without leaving the page. */
export async function clearKeycloakSession(): Promise<void> {
    try {
        await userManager().removeUser();
    } catch {
        /* nothing stored, or not configured */
    }
}

/**
 * End the Keycloak session too: the browser goes to Keycloak's end-session
 * endpoint and comes back to `postLogoutPath`. `idTokenHint` is read before the
 * app's own cleanup removes the session; without it Keycloak asks for a
 * confirmation click.
 */
export async function endKeycloakSession(options: { idTokenHint?: string | null; postLogoutPath?: string } = {}): Promise<void> {
    await userManager().signoutRedirect({
        ...(options.idTokenHint ? { id_token_hint: options.idTokenHint } : {}),
        post_logout_redirect_uri: `${window.location.origin}${safeReturnPath(options.postLogoutPath)}`,
    });
}
